/*
 * World clock — the slow heartbeat behind day/night and weather.
 *
 * Fernhollow Square is "always twilight" per CLAUDE.md, but the air is allowed
 * to breathe across a 30-minute real-time cycle of four sub-phases:
 *
 *   dusk          0.00..0.25  — v0.2 baseline twilight (lanterns just starting)
 *   deeptwilight  0.25..0.50  — slightly darker, more fireflies, brighter glow
 *   late          0.50..0.75  — even darker; low ambient hour
 *   dawn          0.75..1.00  — a hair lighter; fewer fireflies; gentler flicker
 *
 * Lore justification (carry this in your head when explaining to a collaborator):
 * the square exists in this hour the way some places exist in a season. The hour
 * itself drifts a little — sometimes the lanterns warm earlier, sometimes the
 * fireflies linger after dawn — but it is always *this* hour.
 *
 * Persistence: the cycle offset is stored under its own localStorage key so the
 * clock position is stable across refreshes within a browser. We deliberately
 * keep this OUT of `SaveData` (the canonical save schema) — the world clock is
 * a per-device ambient property, not part of the player's saved critter.
 */

const STORAGE_KEY = 'fernhollow-world-clock';

/** One full cycle in real-world milliseconds. Exported so it can be tuned. */
export const CYCLE_DURATION_MS = 30 * 60 * 1000;

/** Weather cadence — drizzle starts every ~15 minutes of real time. */
export const WEATHER_PERIOD_MS = 15 * 60 * 1000;

/** Duration of each drizzle event. */
export const WEATHER_DURATION_MS = 90 * 1000;

/**
 * Phase boundaries within the cycle (cycleT in [0, 1)).
 *
 * Exported so consumers (stargaze, sound bed, etc.) can reason about the same
 * boundaries we use here. Order matters: must be ascending and span 0..1.
 */
export const PHASE_BOUNDARIES: ReadonlyArray<{ readonly phase: WorldPhase; readonly start: number }> = [
  { phase: 'dusk', start: 0.0 },
  { phase: 'deeptwilight', start: 0.25 },
  { phase: 'late', start: 0.5 },
  { phase: 'dawn', start: 0.75 },
];

export type WorldPhase = 'dusk' | 'deeptwilight' | 'late' | 'dawn';

export type Weather = 'clear' | 'drizzle';

export interface WorldState {
  /** Which named phase the clock is currently in. */
  phase: WorldPhase;
  /** 0..1 progress through the current phase (0 at start, ~1 at boundary). */
  phaseT: number;
  /** 0..1 progress through the full 30-min cycle. */
  cycleT: number;
  /** Current weather. Only 'clear' and 'drizzle' in MVP. */
  weather: Weather;
  /** 0..1 ramp on weather — 0 at clear, 1 mid-drizzle. Cross-fades for soft entries/exits. */
  weatherT: number;
}

/**
 * Per-phase lantern intensity multiplier. Multiplies the existing flicker so
 * lanterns visibly brighten at deep twilight without becoming a different
 * lighting recipe. Defaults so that the v0.2 baseline (dusk) is exactly 1.0.
 */
const LANTERN_MULTIPLIERS: Readonly<Record<WorldPhase, number>> = {
  dusk: 1.0,
  deeptwilight: 1.3,
  late: 1.5,
  dawn: 0.8,
};

/**
 * Per-phase firefly count multiplier. Capped at +50% extras spawned (see
 * firefly.ts), so values above 1.5 here will saturate at the spawn cap.
 */
const FIREFLY_MULTIPLIERS: Readonly<Record<WorldPhase, number>> = {
  dusk: 1.0,
  deeptwilight: 1.4,
  late: 1.2,
  dawn: 0.6,
};

/**
 * The clock keeps a single offset (ms since epoch when t=0) so the cycle
 * position is a pure function of `Date.now()`. We persist this offset so a
 * refresh resumes mid-phase rather than restarting at dusk.
 */
interface ClockBacking {
  /** Reference timestamp: cycle started at this epoch ms. */
  cycleStartedAt: number;
  /** Drizzle-window scheduling reference. */
  weatherCycleStartedAt: number;
}

let clock: ClockBacking = loadOrInitClock();

/** Most recent computed state. Cached so `currentWorldState` doesn't re-do math when callers query mid-frame. */
let lastState: WorldState = computeStateAt(Date.now());

function loadOrInitClock(): ClockBacking {
  const now = Date.now();
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ClockBacking>;
      const cycleStartedAt =
        typeof parsed.cycleStartedAt === 'number' && Number.isFinite(parsed.cycleStartedAt)
          ? parsed.cycleStartedAt
          : now;
      const weatherCycleStartedAt =
        typeof parsed.weatherCycleStartedAt === 'number' && Number.isFinite(parsed.weatherCycleStartedAt)
          ? parsed.weatherCycleStartedAt
          : now;
      return { cycleStartedAt, weatherCycleStartedAt };
    }
  } catch {
    /* storage unavailable / corrupt — fall through to fresh init */
  }
  const fresh: ClockBacking = { cycleStartedAt: now, weatherCycleStartedAt: now };
  persistClock(fresh);
  return fresh;
}

function persistClock(c: ClockBacking): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  } catch {
    /* swallow — world clock isn't critical enough to surface */
  }
}

/**
 * Phase queries derived from a 0..1 cycleT. Exposed for testability — the
 * runtime uses the cached `lastState` for normal calls.
 */
function phaseAt(cycleT: number): { phase: WorldPhase; phaseT: number } {
  // PHASE_BOUNDARIES is ascending; find the last boundary whose start <= cycleT.
  let chosen = PHASE_BOUNDARIES[0]!;
  let chosenIdx = 0;
  for (let i = 0; i < PHASE_BOUNDARIES.length; i++) {
    const b = PHASE_BOUNDARIES[i]!;
    if (b.start <= cycleT) {
      chosen = b;
      chosenIdx = i;
    }
  }
  const nextStart =
    chosenIdx + 1 < PHASE_BOUNDARIES.length ? PHASE_BOUNDARIES[chosenIdx + 1]!.start : 1.0;
  const span = nextStart - chosen.start;
  const phaseT = span > 0 ? Math.min(1, Math.max(0, (cycleT - chosen.start) / span)) : 0;
  return { phase: chosen.phase, phaseT };
}

/** Pure: derive a full WorldState from a wall-clock timestamp. */
function computeStateAt(nowMs: number): WorldState {
  const cycleElapsed = ((nowMs - clock.cycleStartedAt) % CYCLE_DURATION_MS + CYCLE_DURATION_MS) % CYCLE_DURATION_MS;
  const cycleT = cycleElapsed / CYCLE_DURATION_MS;
  const { phase, phaseT } = phaseAt(cycleT);

  // Weather: a single drizzle window opens at the start of each WEATHER_PERIOD_MS
  // block, lasts WEATHER_DURATION_MS, and cross-fades at both ends (~8s ramps)
  // so it doesn't snap on/off.
  const weatherElapsed =
    ((nowMs - clock.weatherCycleStartedAt) % WEATHER_PERIOD_MS + WEATHER_PERIOD_MS) % WEATHER_PERIOD_MS;
  let weather: Weather = 'clear';
  let weatherT = 0;
  if (weatherElapsed < WEATHER_DURATION_MS) {
    weather = 'drizzle';
    const rampMs = 8000;
    const into = Math.min(1, weatherElapsed / rampMs);
    const outOf = Math.min(1, (WEATHER_DURATION_MS - weatherElapsed) / rampMs);
    weatherT = Math.max(0, Math.min(into, outOf));
  }

  return { phase, phaseT, cycleT, weather, weatherT };
}

/**
 * Advance the world clock by `dtSeconds`. We don't actually need dt to compute
 * the state (it's derived from wall-clock) but accepting dt matches the other
 * render modules and lets us write tests against a faked clock easily.
 */
export function tickWorld(_dtSeconds: number): WorldState {
  lastState = computeStateAt(Date.now());
  return lastState;
}

/** Read the most recently computed world state without re-computing. */
export function currentWorldState(): WorldState {
  return lastState;
}

/**
 * Fast-forward the cycle by `ms` real-world milliseconds. Useful for the
 * acceptance test "sky visibly cycles when fast-forwarded" — call from a debug
 * key binding. Persists the new offset.
 */
export function fastForwardWorld(ms: number): WorldState {
  clock = {
    cycleStartedAt: clock.cycleStartedAt - ms,
    weatherCycleStartedAt: clock.weatherCycleStartedAt - ms,
  };
  persistClock(clock);
  return tickWorld(0);
}

/**
 * Reset the clock to "start of dusk, clear weather, now." Mainly for tests.
 */
export function resetWorldClock(): WorldState {
  const now = Date.now();
  clock = { cycleStartedAt: now, weatherCycleStartedAt: now };
  persistClock(clock);
  return tickWorld(0);
}

/**
 * Lantern intensity multiplier for a given state. Cross-fades across the
 * boundary into the next phase so the brightness change is gradual (cozy tone:
 * no abrupt transitions).
 */
export function lanternIntensityFor(state: WorldState): number {
  const current = LANTERN_MULTIPLIERS[state.phase];
  const next = LANTERN_MULTIPLIERS[nextPhaseOf(state.phase)];
  // Blend into the next phase over the last 25% of the current phase so each
  // phase has ~75% of its time at its own value and ~25% transitioning out.
  const blend = state.phaseT > 0.75 ? (state.phaseT - 0.75) / 0.25 : 0;
  return lerp(current, next, blend);
}

/**
 * Firefly-count multiplier for a given state. Same cross-fade approach as
 * `lanternIntensityFor` so the swarm grows/shrinks smoothly across boundaries.
 */
export function fireflyCountFactorFor(state: WorldState): number {
  const current = FIREFLY_MULTIPLIERS[state.phase];
  const next = FIREFLY_MULTIPLIERS[nextPhaseOf(state.phase)];
  const blend = state.phaseT > 0.75 ? (state.phaseT - 0.75) / 0.25 : 0;
  return lerp(current, next, blend);
}

function nextPhaseOf(p: WorldPhase): WorldPhase {
  switch (p) {
    case 'dusk':
      return 'deeptwilight';
    case 'deeptwilight':
      return 'late';
    case 'late':
      return 'dawn';
    case 'dawn':
      return 'dusk';
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
